import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import PasswordConfirmDialog from "../components/PasswordConfirmDialog";
import ProductAutocomplete from "../components/ProductAutocomplete";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, Plus, Trash2, Loader2, Package } from "lucide-react";

export default function Losses() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [losses, setLosses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({ productId: "", variantId: "" });
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: p }] = await Promise.all([
        api.get("/losses"),
        api.get("/products"),
      ]);
      setLosses(l || []);
      setProducts(p || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const searchOptions = useMemo(() => {
    const rows = [];
    for (const p of products) {
      for (const v of p.variants || []) {
        if (v.stock <= 0) continue;
        rows.push({
          productId: p.id,
          variantId: v.id,
          label: `${p.name} · ${v.name}`,
          stock: v.stock,
          image_base64: v.image_base64 || p.image_base64 || "",
        });
      }
    }
    return rows;
  }, [products]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!sel.productId || !sel.variantId) { toast.error("Escolhe o produto"); return; }
    if (!(Number(quantity) > 0)) { toast.error("Quantidade inválida"); return; }
    setSaving(true);
    try {
      await api.post("/losses", {
        product_id: sel.productId,
        variant_id: sel.variantId,
        quantity: Number(quantity),
        reason: reason.trim(),
      });
      toast.success("Quebra registada · stock descontado");
      setSel({ productId: "", variantId: "" });
      setQuantity(1);
      setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registar");
    } finally { setSaving(false); }
  };

  const totalCost = losses.reduce((a, l) => a + (Number(l.cost) || 0), 0);
  const totalQty = losses.reduce((a, l) => a + (Number(l.quantity) || 0), 0);

  return (
    <div className="min-h-screen bg-background" data-testid="losses-page">
      <DashboardHeader title="Quebras" />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="lb-fade-in">
          <div className="lb-overline">Produtos danificados / perdidos</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Quebras</h1>
          <p className="text-sm text-muted-foreground mt-1">Retira unidades do stock e contabiliza o custo de compra como perda no painel financeiro.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white lb-shadow-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-[hsl(var(--destructive))]" /> Unidades perdidas</div>
            <div className="mt-1 font-display text-2xl">{totalQty}</div>
          </div>
          <div className="rounded-2xl bg-white lb-shadow-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-[hsl(var(--destructive))]" /> Custo total</div>
            <div className="mt-1 font-display text-2xl text-[hsl(var(--destructive))]">{format(totalCost)}</div>
          </div>
        </div>

        {/* Register form */}
        <form onSubmit={submit} className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5 space-y-3">
          <div className="lb-overline">Nova quebra</div>
          <div className="space-y-2">
            <Label>Produto e variante</Label>
            <ProductAutocomplete options={searchOptions} value={sel} onChange={setSel} placeholder="Escrever produto…" testid="loss-product-autocomplete" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Quantidade danificada</Label>
              <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="loss-qty-input" />
            </div>
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: quebrado no envio" data-testid="loss-reason-input" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="bg-[hsl(var(--destructive))] hover:bg-[#B03838] text-white" data-testid="add-loss-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Registar quebra
          </Button>
        </form>

        {/* List */}
        {loading ? (
          <div className="space-y-2">{Array.from({length:3}).map((_,i)=><div key={i} className="h-14 bg-white rounded-xl lb-shadow-card animate-pulse" />)}</div>
        ) : losses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl lb-shadow-card" data-testid="empty-losses">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground text-sm">Ainda sem quebras registadas.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {losses.map((l) => (
              <div key={l.id} className="bg-white rounded-xl lb-shadow-card p-3 flex items-center gap-3" data-testid={`loss-row-${l.id}`}>
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--destructive))]/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{l.product_name} · {l.variant_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {l.quantity} un. · {new Date(l.created_at).toLocaleDateString("pt-PT")}
                    {l.reason && ` · ${l.reason}`}
                  </div>
                </div>
                <div className="font-display text-lg text-[hsl(var(--destructive))]">− {format(l.cost)}</div>
                <Button variant="ghost" size="icon" onClick={() => setDeleteId(l.id)} data-testid={`delete-loss-${l.id}`}>
                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-[hsl(var(--destructive))]" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>

      <PasswordConfirmDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Eliminar quebra"
        description="Restaura as unidades ao stock e reverte a alocação FIFO."
        actionLabel="Eliminar quebra"
        onConfirm={async (password) => {
          await api.delete(`/losses/${deleteId}`, { headers: { "X-Confirm-Password": password } });
          toast.success("Quebra eliminada · stock reposto");
          setDeleteId(null);
          load();
        }}
      />
    </div>
  );
}
